import {Box, Heading, HStack, Icon, Text, type ThemeIconKey} from 'platform/foundation';
import styled from 'styled-components';

import i18n from '@dms/i18n';

import {
  type Specification,
  type getVehicleSpecifications,
} from '../../../utils/getVehicleSpecifications';
import {ADVERTISEMENT_PREVIEW_SECTION_ICONS} from '../constants/ADVERTISEMENT_PREVIEW_SECTION_ICONS';
import {PREVIEW_CONTAINER_BREAKPOINTS} from '../constants/PREVIEW_CONTAINER_BREAKPOINTS';
import {getPreviewContainerBreakpoint} from '../utils/getPreviewContainerBreakpoint';
import {SectionCard} from './PreviewSectionCard';

const GROUP_ICONS: Record<string, {icon: ThemeIconKey; isNotFilled?: boolean}> = {
  history: {icon: 'action/history_stroke', isNotFilled: true},
  vehicle: {icon: 'automotive/car_stroke'},
  engine: {icon: 'automotive/gauge_stroke'},
  service: {icon: 'content/clipboard_check_stroke', isNotFilled: true},
  dimensions: {icon: 'specification/ruler_stroke', isNotFilled: true},
  consumption: {icon: 'specification/fuel_pump_thin', isNotFilled: true},
};
const FALLBACK_GROUP_ICON = GROUP_ICONS.vehicle;
const CARD_ICON_SIZE = 5;
const ICON_COLOR = 'general.accent';

export function AdvertisementPreviewSpecifications(props: {
  specifications: ReturnType<typeof getVehicleSpecifications>;
  'data-testid': string;
}) {
  return (
    <SectionCard data-testid={`${props['data-testid']}-cardWrapper-box`}>
      <SectionHeading>
        <Icon
          value={ADVERTISEMENT_PREVIEW_SECTION_ICONS.details}
          size={5}
          color={ICON_COLOR}
          isNotFilled
        />
        <Heading size={4}>{i18n.t('page.advertisement.preview.technicalData')}</Heading>
      </SectionHeading>
      <DetailsGrid>
        {props.specifications.groups.map((group) => (
          <DetailsGroup key={group.id}>
            <Box paddingBottom={3}>
              <HStack align="center" spacing={2}>
                <Icon
                  value={(GROUP_ICONS[group.id] ?? FALLBACK_GROUP_ICON).icon}
                  size={CARD_ICON_SIZE}
                  color={ICON_COLOR}
                  isNotFilled={(GROUP_ICONS[group.id] ?? FALLBACK_GROUP_ICON).isNotFilled}
                />
                <Heading size={4} headingLevel={3}>
                  {group.title}
                </Heading>
              </HStack>
            </Box>
            <DetailsList>
              {group.rows.map((row: Specification) => (
                <DetailRow key={row.label}>
                  <Box>
                    <Text size="small" color="secondary">
                      {row.label}
                    </Text>
                  </Box>
                  <DetailValue>
                    <Text size="small" color="primary">
                      <strong>{row.value}</strong>
                    </Text>
                  </DetailValue>
                </DetailRow>
              ))}
            </DetailsList>
          </DetailsGroup>
        ))}
      </DetailsGrid>
    </SectionCard>
  );
}

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  padding: ${({theme}) => theme.getSize(6)} ${({theme}) => theme.getSize(6)} 0;
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.compact
  )}) {
    padding: ${({theme}) => theme.getSize(4)} ${({theme}) => theme.getSize(4)} 0;
  }
`;
const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${({theme}) => theme.getSize(4)};
  padding: ${({theme}) => theme.getSize(5)} ${({theme}) => theme.getSize(6)}
    ${({theme}) => theme.getSize(6)};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.tablet
  )}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.phone
  )}) {
    grid-template-columns: minmax(0, 1fr);
    padding: ${({theme}) => theme.getSize(4)};
  }
`;
const DetailsGroup = styled.div`
  min-width: 0;
  align-self: start;
  padding: ${({theme}) => theme.getSize(4)};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.palettes.neutral[20][100]};
`;
const DetailsList = styled.div`
  padding: ${({theme}) => theme.getSize(1)} 0 0;
  border-top: 1px solid ${({theme}) => theme.colors.general.separator};
`;
const DetailRow = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: ${({theme}) => theme.getSize(3)};
  padding: ${({theme}) => theme.getSize(2)} 0;
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.row
  )}) {
    flex-direction: column;
    gap: ${({theme}) => theme.getSize(1)};
  }
`;
const DetailValue = styled.div`
  max-width: 70%;
  text-align: right;
  overflow-wrap: anywhere;
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.row
  )}) {
    max-width: 100%;
    text-align: left;
  }
`;
